import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b border-[#DBEAFE]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link 
            to="/auth" 
            className="inline-flex items-center text-[#6B7280] hover:text-[#2C5F7C] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign Up
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-[#DBEAFE] p-8 md:p-12">
          <h1 className="text-4xl font-bold text-[#111827] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CostCraft
          </h1>
          <p className="text-[#6B7280] mb-8">Terms & Conditions and Privacy Policy</p>

          {/* Terms & Conditions */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-[#111827] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Terms & Conditions
            </h2>
            
            <p className="text-sm text-[#6B7280] mb-6">
              <strong>Effective Date:</strong> 01 February 2026<br />
              <strong>Provider:</strong> Bellarine Tech Assist ("we", "us", "our")<br />
              <strong>Product:</strong> CostCraft ("the Service")
            </p>

            <div className="space-y-6 text-[#1F2937]">
              <div>
                <h3 className="font-semibold text-[#111827] mb-2">1. Acceptance of Terms</h3>
                <p>By creating an account or using CostCraft, you agree to these Terms & Conditions. If you do not agree, you must not use the Service.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">2. Service Overview</h3>
                <p>CostCraft provides tools for estimating baking and recipe costs, ingredient pricing, and profitability calculations. The Service is provided for informational and business support purposes only.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">3. User Responsibility</h3>
                <p className="mb-2">You are responsible for:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Ensuring ingredient costs are entered accurately</li>
                  <li>Reviewing outputs before making pricing decisions</li>
                  <li>Maintaining confidentiality of your login details</li>
                  <li>Any activity that occurs under your account</li>
                </ul>
                <p className="mt-2 text-[#6B7280] italic">CostCraft does not guarantee that calculated results will be correct for your business circumstances.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">4. Subscription & Billing</h3>
                <p className="mb-2">CostCraft may offer subscription tiers, including trial periods.</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Subscription fees are billed in advance</li>
                  <li>Trials may convert into paid plans unless cancelled</li>
                  <li>Pricing may change with notice provided inside the Service</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">5. Cancellation</h3>
                <p>You may cancel your subscription at any time. Access will remain active until the end of your billing period. Refunds are handled in accordance with Australian Consumer Law.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">6. Service Availability</h3>
                <p className="mb-2">We aim to provide reliable access, however:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>The Service may be unavailable due to maintenance, outages, or external factors</li>
                  <li>We do not guarantee uninterrupted access</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">7. Data & Backups</h3>
                <p className="mb-2">We take reasonable steps to protect user data, but you acknowledge:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>No software system is immune from failure</li>
                  <li>You are encouraged to maintain your own backups or exports of important business data</li>
                  <li>We are not liable for loss of data or interruption of access</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">8. Disclaimer (Cost Estimates Only)</h3>
                <p>CostCraft provides estimates only. All outputs are dependent on user-entered data and assumptions. We do not provide accounting, financial, or business advice.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">9. Limitation of Liability</h3>
                <p className="mb-2">To the maximum extent permitted by law, Bellarine Tech Assist is not liable for any indirect or consequential loss, including:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Loss of profit</li>
                  <li>Business interruption</li>
                  <li>Lost sales or missed opportunities</li>
                </ul>
                <p className="mt-2">Our total liability is limited to the amount paid by you in the last 30 days.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">10. Australian Consumer Law</h3>
                <div className="bg-[#D1FAE5] border border-[#10B981] rounded-lg p-4 mb-2">
                  <p className="text-[#065F46]"><strong>Your Rights:</strong> Our goods and services come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled to a replacement or refund for a major failure and compensation for any other reasonably foreseeable loss or damage.</p>
                </div>
                <p className="text-[#6B7280]">Nothing in these terms limits your rights under the Australian Consumer Law. For more information, visit the <a href="https://www.accc.gov.au/consumers" className="text-[#2C5F7C] hover:underline" target="_blank" rel="noopener noreferrer">ACCC website</a>.</p>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">11. Termination</h3>
                <p className="mb-2">We may suspend or terminate access if:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>These terms are breached</li>
                  <li>Misuse or harmful activity is detected</li>
                  <li>Required payments are not completed</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">11. Governing Law</h3>
                <p>These Terms are governed by the laws of Victoria, Australia.</p>
              </div>
            </div>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className="mb-12 pt-8 border-t border-[#DBEAFE]">
            <h2 className="text-2xl font-semibold text-[#111827] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Privacy Policy
            </h2>
            
            <p className="text-[#6B7280] mb-6">CostCraft is operated by Bellarine Tech Assist.</p>

            <div className="space-y-6 text-[#1F2937]">
              <div>
                <h3 className="font-semibold text-[#111827] mb-2">Information We Collect</h3>
                <p className="mb-2">We may collect:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Name and email address</li>
                  <li>Ingredient and recipe data you enter</li>
                  <li>Login activity and basic analytics</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">What We Do NOT Collect</h3>
                <p className="mb-2">Currently, CostCraft does not collect:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Customer sales data</li>
                  <li>Payment details directly (handled by Stripe when enabled)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">How Data Is Used</h3>
                <p className="mb-2">We use collected data only to:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Provide the Service</li>
                  <li>Improve features and performance</li>
                  <li>Support account management</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">Sharing</h3>
                <p className="mb-2">We do not sell your data. We may share limited data with trusted providers such as:</p>
                <ul className="list-disc list-inside space-y-1 text-[#6B7280]">
                  <li>Hosting platforms</li>
                  <li>Payment processors (e.g., Stripe)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#111827] mb-2">Security</h3>
                <p>We take reasonable security measures, but no system can guarantee absolute protection.</p>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="pt-8 border-t border-[#DBEAFE]">
            <h2 className="text-2xl font-semibold text-[#111827] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Disclaimer
            </h2>
            <p className="text-[#6B7280] bg-[#EFF6FF] rounded-lg p-4">
              CostCraft provides recipe costing estimates based on user-entered ingredient pricing. All results should be independently verified before making business decisions. Bellarine Tech Assist accepts no responsibility for financial loss, pricing outcomes, or business interruption resulting from use of the Service.
            </p>
          </section>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-[#DBEAFE] text-center">
            <p className="text-sm text-[#6B7280]">
              © 2026 Bellarine Tech Assist. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
